/**
 * Weekday grouping and time formatting for recurring timetables.
 *
 * Shared by the admin dashboard's Weekly Timetable and the tutor dashboard's teaching week. It is
 * deliberately pure data helpers: the two dashboards keep separate *components* (see the header of
 * pages/Tutor/components/TutorPanel.jsx), but there is no reason for them to disagree on what
 * "Monday" sorts as.
 */

export const WEEKDAY_ORDER = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
];

/** Index within WEEKDAY_ORDER, or Infinity so anything unrecognised sorts to the end. */
export const weekdayIndex = (name) => {
  const i = WEEKDAY_ORDER.indexOf(String(name || "").trim().toLowerCase());
  return i === -1 ? Number.POSITIVE_INFINITY : i;
};

export const todayWeekday = () =>
  new Date().toLocaleDateString("en-GB", { weekday: "long" }).toLowerCase();

/**
 * Group rows by weekday name, Monday first. Unrecognised weekdays keep their own bucket at the end
 * rather than being dropped — a mis-seeded week_days row should be visible, not invisible.
 *
 * `nameOf` reads the weekday from a row: the admin endpoint returns it as a string (`week_days`),
 * the tutor endpoint as the nested relation (`week_days.name`).
 */
export const groupByWeekday = (rows, nameOf) =>
  Object.values(
    (rows || []).reduce((acc, row) => {
      const name = nameOf(row) || "Unscheduled";
      const key = name.toLowerCase();
      if (!acc[key]) acc[key] = { key, name, rows: [] };
      acc[key].rows.push(row);
      return acc;
    }, {})
  ).sort((a, b) => weekdayIndex(a.name) - weekdayIndex(b.name) || a.name.localeCompare(b.name));

/** "09:00:00" / "9:00 AM" -> a sortable minute count; unparseable values sort last. */
export const minutesOfDay = (value) => {
  const m = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!m) return Number.POSITIVE_INFINITY;
  let hours = Number(m[1]);
  const mins = Number(m[2]);
  if (/pm/i.test(value) && hours < 12) hours += 12;
  if (/am/i.test(value) && hours === 12) hours = 0;
  return hours * 60 + mins;
};

/** Drop trailing seconds: "14:30:00" -> "14:30". Anything else is passed through untouched. */
export const shortTime = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  return raw.replace(/^(\d{1,2}:\d{2}):\d{2}$/, "$1");
};

/** Sort a day's rows by start time, in place, and return them. */
export const byStartTime = (rows) =>
  [...(rows || [])].sort((a, b) => minutesOfDay(a.start_time) - minutesOfDay(b.start_time));
